import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const initialState = {
  poojaData: [],
  selectedPooja: null,
  status: 'idle',
  loading: false,
  error: null,
  pagination: {
    totalDocs: 0,
    totalPages: 1,
    page: 1,
    limit: 10,
  },
};

// ─── Fetch all poojas ───────────────────────────────────────────────────────
export const fetchPoojas = createAsyncThunk(
  'pooja/fetchPoojas',
  async ({ page, limit, searchBy, search, status, startDate, endDate, bookingOpenTill, createdAt }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('astroVani-admin-token');
      const queryParams = new URLSearchParams({
        page: page,
        limit: limit,
      });

      if (searchBy && search) {
        queryParams.append(searchBy, search);
      }
      if (status === 'active') {
        queryParams.append('isActive', 'true');
      } else if (status === 'inactive') {
        queryParams.append('isActive', 'false');
      }
      if (startDate) queryParams.append('startDateTime', startDate);
      if (endDate) queryParams.append('endDateTime', endDate);
      if (bookingOpenTill) queryParams.append('bookingOpenTill', bookingOpenTill);
      if (createdAt) queryParams.append('createdAt', createdAt);

      const url = `${process.env.REACT_APP_BASEURL}/admin/pooja?${queryParams.toString()}`;
      const response = await fetch(url, { headers: { Authorization: token } });
      if (!response.ok) throw new Error('Failed to fetch poojas');
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ─── Create pooja ────────────────────────────────────────────────
// POST /admin/pooja/
export const createPooja = createAsyncThunk(
  'pooja/createPooja',
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('astroVani-admin-token');
      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/pooja`, {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data?.message || 'Something went wrong');
      return data?.data || data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// ─── Fetch single pooja by ID ───────────────────────────────────────────────
// GET /admin/pooja/:id
export const fetchPoojaById = createAsyncThunk(
  'pooja/fetchPoojaById',
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('astroVani-admin-token');
      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/pooja/${id}`, {
        method: 'GET',
        headers: { Authorization: token },
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data?.message || 'Something went wrong');
      return data?.data || data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// ─── Toggle pooja active status ─────────────────────────────────────────────
// PATCH /admin/pooja/toggleStatus/:id
export const togglePoojaStatus = createAsyncThunk(
  'pooja/togglePoojaStatus',
  async ({ id, isActive }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('astroVani-admin-token');
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/pooja/toggleStatus/${id}`,
        {
          method: 'PATCH',
          headers: { Authorization: token, 'Content-Type': 'application/json' },
          body: JSON.stringify({ isActive }),
        }
      );
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data?.message || 'Failed to toggle status');
      return { id, isActive };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// ─── Update pooja ────────────────────────────────────────────────────────────
// PATCH /admin/pooja/:id
export const updatePooja = createAsyncThunk(
  'pooja/updatePooja',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('astroVani-admin-token');
      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/pooja/${id}`, {
        method: 'PATCH',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data?.message || 'Something went wrong');
      return { id, data: data.data || data };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// ─── Create pooja–astrologer mapping ────────────────────────────────────────
// POST /admin/pooja/mapping
export const createPoojaMapping = createAsyncThunk(
  "pooja/createPoojaMapping",
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem(
        "astroVani-admin-token"
      );

      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/pooja/mapping`,
        {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(
          data?.message || "Failed to create mapping"
        );
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Network error"
      );
    }
  }
);

export const fetchPoojaMappings = createAsyncThunk(
  "pooja/fetchPoojaMappings",
  async ({ page, limit }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("astroVani-admin-token");

      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/pooja/mapping?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: token,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(
          data?.message || "Failed to fetch mappings"
        );
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);



// ─── Slice ───────────────────────────────────────────────────────────────────
const poojaSlice = createSlice({
  name: 'pooja',
  initialState,
  reducers: {
    clearSelectedPooja: (state) => {
      state.selectedPooja = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchPoojas.pending, (state) => { state.loading = true; state.status = 'loading'; })
      .addCase(fetchPoojas.fulfilled, (state, action) => {
        state.loading = false;
        state.status = 'succeeded';
        state.poojaData = action.payload?.data || [];
        state.pagination = action.payload?.paginationDetail || initialState.pagination;
      })
      .addCase(fetchPoojas.rejected, (state, action) => { state.loading = false; state.status = 'failed'; state.error = action.payload; })

      // Fetch by ID
      .addCase(fetchPoojaById.pending, (state) => { state.loading = true; })
      .addCase(fetchPoojaById.fulfilled, (state, action) => { state.loading = false; state.selectedPooja = action.payload || null; })
      .addCase(fetchPoojaById.rejected, (state, action) => { state.loading = false; state.selectedPooja = null; state.error = action.payload; })

      // Toggle status
      .addCase(togglePoojaStatus.fulfilled, (state, action) => {
        const idx = state.poojaData.findIndex((p) => p._id === action.payload.id);
        if (idx !== -1) state.poojaData[idx].isActive = action.payload.isActive;
      })
      .addCase(togglePoojaStatus.rejected, (state, action) => { state.error = action.payload; })

      // Update
      .addCase(updatePooja.fulfilled, (state, action) => {
        const idx = state.poojaData.findIndex((p) => p._id === action.payload.id);
        if (idx !== -1) state.poojaData[idx] = { ...state.poojaData[idx], ...action.payload.data };
      })
      .addCase(updatePooja.rejected, (state, action) => { state.error = action.payload; })

      // Create
      .addCase(createPooja.fulfilled, (state, action) => {
        // Just let it complete; we reload from API
      })
      .addCase(createPooja.rejected, (state, action) => { state.error = action.payload; })

      // Create mapping – no state update needed
      .addCase(createPoojaMapping.rejected, (state, action) => { state.error = action.payload; });
  },
});

export const { clearSelectedPooja } = poojaSlice.actions;
export default poojaSlice.reducer;
